import { useCallback } from 'react';
import { useMetadataStore } from '@/entities/metadata/model/metadataStore';
import { useOpenScreen } from './useOpenScreen';
import type { UserScreenNode } from '@/shared/types/system';

/** Menü ağacında verilen nesneyi gösteren ilk ekranı bulur. */
function findByObject(nodes: UserScreenNode[], objectId: string): UserScreenNode | null {
  for (const n of nodes) {
    if ((n.objectId ?? '').toLowerCase() === objectId.toLowerCase()) return n;
    const hit = findByObject(n.children, objectId);
    if (hit) return hit;
  }
  return null;
}

/**
 * Bir NESNEYİ uygulamanın kendi sekmesinde açar.
 *
 * Sekmeler ekrana bağlı (menüdeki yetkili ekranlar); nesnenin ekranı yoksa açılacak
 * bir sekme de yok — bu yüzden kanca `null` döner ve çağıran düğmeyi hiç göstermez.
 * "Açar ama boş ekran gelir" olmasın diye karar çağrı yerinde veriliyor.
 */
export function useOpenObjectScreen() {
  const menuTree = useMetadataStore((s) => s.menuTree);
  const openScreen = useOpenScreen();

  const screenFor = useCallback(
    (objectId: string | null | undefined) => (objectId ? findByObject(menuTree, objectId) : null),
    [menuTree],
  );

  const open = useCallback(
    (objectId: string | null | undefined) => {
      const node = screenFor(objectId);
      if (node) openScreen(node);
      return !!node;
    },
    [screenFor, openScreen],
  );

  /**
   * Nesnenin ekranını her zaman YENİ bir sekmede, verilen filtreyle açar (#445).
   *
   * Popup'ın "yeni sekmede aç" düğmesi için: hedef ekran zaten açıksa (çoğu zaman işlemi
   * çalıştıran ekranın kendisi) `open` yalnızca o sekmeyi odaklıyordu — kullanıcı için
   * "hiçbir şey olmadı". Filtre de o sekmeye taşınamazdı; ayrı sekme ikisini de çözer.
   */
  const openFiltered = useCallback(
    (
      objectId: string | null | undefined,
      filter: { whereClause?: string | null; hedefFilter?: string | null; hedefFilterValue?: unknown },
      opts?: { withDetails?: boolean },
    ) => {
      const found = screenFor(objectId);
      if (!found) return false;
      /*
       * Nesnenin detay sekmeleri varsa sekme master-detay açılır (#445): menüdeki ekran tek
       * grid tipindeyse popup detaylı görünürken sekmede yalnız master geliyordu.
       * MasterDetailScreen detay yoksa zaten tek grid'e düşüyor.
       */
      const node =
        opts?.withDetails && (found.screenTypeName ?? '').toLowerCase() === 'master'
          ? { ...found, screenTypeName: 'MasterDetail' }
          : found;
      const params: Record<string, unknown> = {};
      if (filter.whereClause) params['whereClause'] = filter.whereClause;
      if (filter.hedefFilter) {
        params['hedefFilter'] = filter.hedefFilter;
        params['hedefFilterValue'] = filter.hedefFilterValue;
      }
      openScreen(node, params, { focusExisting: false });
      return true;
    },
    [screenFor, openScreen],
  );

  return { screenFor, open, openFiltered };
}
